import { BeforePseudoIconBorder } from 'accessibility/focusStyles'
import classnames from 'classnames'
import { Megaphone } from 'components/lib/icons'
import RecipientAvatar from 'components/RecipientAvatar'
import useActiveAnnouncementCampaign from 'hooks/useActiveAnnouncementCampaign'
import useActiveCampaign from 'hooks/useActiveCampaign'
import useEmbedThemeProperties from 'hooks/useEmbedThemeProperties'
import useIsBotMessage from 'hooks/useIsBotMessage'
import useIsMobile from 'hooks/useIsMobile'
import useKeyboardNavigation from 'hooks/useKeyboardNavigation'
import useLastAgentMessage from 'hooks/useLastAgentMessage'
import useOnHover from 'hooks/useOnHover'
import useThemeColors from 'hooks/useThemeColors'
import useThemeIcon from 'hooks/useThemeIcon'
import useUnreadMessagesForActiveConversation from 'hooks/useUnreadMessagesForActiveConversation'
import useWidgetAlignment from 'hooks/useWidgetAlignment'
import useWidgetIconShape from 'hooks/useWidgetIconShape'
import { triggerPlaybookClickedAPIEvent } from 'modules/api/events'
import { storeAnnouncementCampaignCompleted } from 'modules/campaigns'
import { showWidgetController } from 'modules/view'
import { toggleChat } from 'modules/view/actions'
import { shouldWidgetIconDisplay } from 'modules/view/selectors'
import { pathOr, uniq } from 'ramda'
import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CampaignDisplayType } from 'types/campaigns'
import { broadcast } from 'utils/message'
import { playSoundNotification } from 'utils/notification'
import { shadeHexColor } from 'utils/styles'

import './style.css'

const ICON_TYPES = {
  SENDER: "SENDER",
  MEGAPHONE: "MEGAPHONE",
  THEME_ICON: "THEME_ICON"
}


const WidgetIcon = () => {
  const buttonRef = useRef(null)
  const isWidgetKeyboardFocused = useKeyboardNavigation(buttonRef)

  const { hoverProps, isHovered } = useOnHover()
  const dispatch = useDispatch()

  const open = useSelector(state =>
    state.view.chatOpen
  )

  const gdpr = useSelector(state =>
    state.session.gdpr
  )

  const shouldWidgetIconShow = useSelector(shouldWidgetIconDisplay)

  const hasActiveConversation = useSelector(state =>
    !!state.conversations.activeConversation
  )

  const widgetAlignment = useWidgetAlignment()

  const activeCampaign = useActiveCampaign()

  const { unreadBadgeEnabled } = useSelector(state =>
    pathOr(false, ['embed', 'configuration', 'theme'], state)
  )

  const unreadMessages = useUnreadMessagesForActiveConversation()

  const isChatTakeover = useSelector(state =>
    state.view.isChatTakeover
  )

  const lastAgentMessage = useLastAgentMessage()
  const isBotMessage = useIsBotMessage(lastAgentMessage)

  const { activeColor, backgroundStyles, widgetSecondaryColor } = useThemeColors()
  const backgroundColor = useMemo(() =>
    isHovered
      ? shadeHexColor(backgroundStyles.background as string, -35)
      : backgroundStyles.background,
    [isHovered, backgroundStyles]
  )
  const { Icon, customIcon, hasCustomIcon } = useThemeIcon()
  const { senderId, type } = useActiveAnnouncementCampaign()

  const agentAvatarId = senderId || pathOr(undefined, ['authorId'], lastAgentMessage)

  const totalUnreadMessages = unreadMessages ? uniq(unreadMessages).length : 0

  const isSlider = type === CampaignDisplayType.SLIDER

  const useAvatarIcon = (agentAvatarId && (isSlider || totalUnreadMessages > 0))

  const [titleNotificationsEnabled = false] = useEmbedThemeProperties(['titleNotificationEnabled'])

  const iconType = useMemo(() => {
    if (useAvatarIcon) {
      return ICON_TYPES.SENDER
    }

    if (isSlider && !agentAvatarId) {
      return ICON_TYPES.MEGAPHONE
    }

    return ICON_TYPES.THEME_ICON
  }, [agentAvatarId, isSlider, useAvatarIcon])

  const isMobile = useIsMobile()
  const widgetIconShape = useWidgetIconShape()

  useEffect(() => {
    if (!titleNotificationsEnabled) {
      return
    }

    broadcast({
      topic: 'page-notification-update',
      message: {
        totalUnreadMessages
      }
    })
  }, [totalUnreadMessages, titleNotificationsEnabled])

  const toggle = isOpen => {
    if (isSlider) {
      broadcast({
        topic: 'slider:showDetails',
        message: {}
      })
    } else {
      dispatch(toggleChat({ chatOpen: isOpen }))
    }

    if (isOpen) {
      broadcast({ topic: 'reset-controller-size' })
    }
  }

  const onToggleChat = () => {
    if (!open) {
      if (totalUnreadMessages > 0 && isBotMessage) {
        const playbookId = pathOr(null, ['attributes', 'playbookId'], activeCampaign)

        storeAnnouncementCampaignCompleted(activeCampaign.id, playbookId)
        triggerPlaybookClickedAPIEvent(lastAgentMessage)
      }
    }

    toggle(!open)
  }

  useEffect(() => {
    if (shouldWidgetIconShow) {
      showWidgetController()
    }
  }, [shouldWidgetIconShow])

  useEffect(() => {
    broadcast({
      topic: 'toggle-widget-controller',
      message: {
        isChatTakeover,
        open: open,
        hasActiveConversation: hasActiveConversation && gdpr.hasConsent
      }
    })
  }, [open, gdpr, hasActiveConversation, isChatTakeover])

  useEffect(() => {
    if (unreadMessages && unreadMessages.length > 0) {
      playSoundNotification()
    }
  }, [unreadMessages])

  const buttonStyles = useMemo(() => {
    if (useAvatarIcon && !open) {
      return null
    }

    return { background: `${backgroundColor}` }
  }, [useAvatarIcon, open, backgroundColor])

  const iconStyle = useMemo(() => {
    if (!hasCustomIcon || useAvatarIcon || open) {
      return null
    }

    return isHovered ?
      { opacity: 0.65, ...customIcon } :
      customIcon
  }, [customIcon, useAvatarIcon, open, hasCustomIcon, isHovered])


  const buttonAriaLabel = useMemo(() => {
    if (open) {
      return 'Click to close chat'
    }

    const openLabel = 'Click to open chat'

    if (unreadBadgeEnabled && totalUnreadMessages > 0) {
      return `${openLabel} - Unread messages: ${totalUnreadMessages}`
    }

    return openLabel
  }, [open, unreadBadgeEnabled, totalUnreadMessages])

  return (
    <>
      <style>
        {`
          .drift-widget-controller.keyboard-focused:before {
            ${BeforePseudoIconBorder(activeColor,64) }
          }
        `}
      </style>
      <button
        className={classnames(`drift-widget-controller drift-widget-controller--align-${widgetAlignment}`, [widgetIconShape],
          {
            "drift-widget-controller--closed": !isMobile && open,
            "drift-widget-controller--custom-icon": hasCustomIcon,
            "drift-widget-controller--avatar": useAvatarIcon,
            "drift-widget-controller--hide": isChatTakeover,
            "keyboard-focused": isWidgetKeyboardFocused
        })}
        onClick={onToggleChat}
        style={buttonStyles}
        {...hoverProps}
        aria-label={buttonAriaLabel}
        ref={buttonRef}
      >
        <div
          className={classnames("drift-widget-controller-icon", [widgetIconShape])}
          style={iconStyle}
        >
          <div className="drift-controller-icon--active">
            { iconType === ICON_TYPES.MEGAPHONE &&
              <Megaphone fill={widgetSecondaryColor} />
            }
            { iconType === ICON_TYPES.SENDER &&
              <RecipientAvatar
              className={classnames([widgetIconShape], "drift-controller-icon--avatar")}
              avatarClassName={classnames([widgetIconShape], "drift-controller-icon--avatar-avatar")}
                id={agentAvatarId}
              />
            }
            { iconType === ICON_TYPES.THEME_ICON && !hasCustomIcon && Icon }
          </div>
          <div className="drift-controller-icon--close" style={{ backgroundColor: widgetSecondaryColor }}></div>
        </div>
        {unreadBadgeEnabled && totalUnreadMessages > 0 &&
          <div className="drift-controller-icon-unread">
            {Math.min(totalUnreadMessages, 99)}
          </div>
        }
      </button>
    </>
  )
}

export default WidgetIcon
